import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import { Search, Calendar, Users, ChevronLeft, ChevronRight, XCircle } from "lucide-react";
import { attendanceApi } from "../../api/services";

const ClassSelection = ({
  onClassSelect,
  availableClasses = [],
  selectedDate,
  onDateChange,
}) => {
  const { isTeacher, isAdmin } = useAuth();
  const { currentAcademicYear } = useData();
  const [assignedClasses, setAssignedClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchOverview = async () => {
      setLoading(true);
      try {
        const response = await attendanceApi.getAttendanceOverview(selectedDate, currentAcademicYear);
        if (response.success && response.data) {
          // Merge API data with local section data
          const mergedData = response.data.map((apiItem) => {
            const localClass = availableClasses.find(c => c._id === apiItem._id);
            return {
              ...apiItem,
              unitName: apiItem.className,
              subject: apiItem.description || "General",
              totalStudents: apiItem.studentsCount,
              // Check if API returns these, otherwise default to 0/undefined
              present: apiItem.presentCount || 0,
              absent: apiItem.absentCount || 0,
              isTaken: apiItem.status === "Done",
              isHoliday: apiItem.status === "Holiday",
              sections: localClass?.sections || [], // Critical for modal
            };
          });
          setAssignedClasses(mergedData);
        } else {
             setAssignedClasses([]);
        }
      } catch (error) {
        console.error("Failed to fetch attendance overview", error);
        setAssignedClasses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, [selectedDate, currentAcademicYear, availableClasses]);

  /* Section Selection Modal */
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);

  const handleClassClick = (cls) => {
    if (cls.sections && cls.sections.length > 0) {
      setSelectedUnit(cls);
      setShowSectionModal(true);
    } else {
      onClassSelect(cls, selectedDate);
    }
  };

  const handleSectionSelect = (section) => {
    onClassSelect(selectedUnit, selectedDate, section);
    setShowSectionModal(false);
    setSelectedUnit(null);
  };

  const filteredClasses = assignedClasses.filter(cls => 
    cls.unitName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cls.subject && cls.subject.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
  };

  if (!isTeacher && !isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h1 className="mb-2 text-2xl font-bold text-gray-900">Access Denied</h1>
          <p className="text-gray-600">Only teachers and admins can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Select Class</h2>
            <p className="text-xs text-gray-500">Choose a class to mark attendance</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Date Selector */}
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
              <button 
                  onClick={() => {
                      const d = new Date(selectedDate);
                      d.setDate(d.getDate() - 1);
                      onDateChange(d.toISOString().split('T')[0]);
                  }}
                  className="p-1 hover:bg-gray-200 rounded-full text-gray-500"
              >
                  <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="relative mx-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>{formatDate(selectedDate)}</span>
                  </div>
                  <input 
                    type="date" 
                    value={selectedDate}
                    onChange={(e) => onDateChange(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full"
                  />
              </div>
              <button 
                  onClick={() => {
                      const d = new Date(selectedDate);
                      d.setDate(d.getDate() + 1);
                      onDateChange(d.toISOString().split('T')[0]);
                  }}
                  className="p-1 hover:bg-gray-200 rounded-full text-gray-500"
              >
                  <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                  type="text"
                  placeholder="Search class..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded w-full sm:w-64 input-field"
              />
            </div>
          </div>
        </div>

        <div className="p-4 bg-gray-50 min-h-[400px]">
          {loading ? (
            <LoadingGrid />
          ) : filteredClasses.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredClasses.map((cls) => {
                  const isTaken = cls.isTaken;
                  const isHoliday = cls.isHoliday;
                  
                  return (
                    <div
                      key={cls._id}
                      onClick={() => handleClassClick(cls)}
                      className={`bg-white rounded border border-gray-200 cursor-pointer hover:shadow-md transition-all relative overflow-hidden group`}
                      onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--primary-color, #4f46e5)'}
                      onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                    >
                      <div className={`absolute top-0 left-0 w-1 h-full ${isTaken ? 'bg-green-500' : isHoliday ? 'bg-amber-500' : 'bg-gray-300'}`} style={(!isTaken && !isHoliday) ? { '--hover-bg': 'var(--primary-color, #4f46e5)' } : {}} onMouseOver={(e) => (!isTaken && !isHoliday) && (e.currentTarget.style.backgroundColor = 'var(--primary-color, #4f46e5)')} onMouseOut={(e) => (!isTaken && !isHoliday) && (e.currentTarget.style.backgroundColor = '')} />
                      
                      <div className="p-3 pl-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                              <h3 className="text-sm font-bold text-gray-900 transition-colors" onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-color, #4f46e5)'} onMouseOut={(e) => e.currentTarget.style.color = ''}>{cls.unitName}</h3>
                              <p className="text-xs text-gray-500 truncate max-w-[150px]">{cls.subject}</p>
                          </div>
                          {isTaken ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700 border border-green-100">
                                  Taken
                              </span>
                          ) : isHoliday ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-100">
                                  Holiday
                              </span>
                          ) : (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                  Pending
                              </span>
                          )}
                        </div>

                        <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                          <div className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" />
                              <span>{cls.totalStudents} Students</span>
                          </div>
                          {cls.sectionsCount > 0 && (
                             <span className="px-1.5 py-0.5 rounded text-[10px] font-medium border" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', color: 'var(--primary-color, #4f46e5)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 20%, white)' }}>
                               {cls.sectionsCount} Sections
                             </span>
                          )}
                        </div>
                        
                        {isTaken && (cls.present > 0 || cls.absent > 0) && (
                          <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-2 gap-2 text-[10px]">
                              <div className="text-green-600 font-medium">Present: {cls.present}</div>
                              <div className="text-red-600 font-medium text-right">Absent: {cls.absent}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Section Selection Modal */}
      {showSectionModal && selectedUnit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-lg shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
              <h3 className="font-bold text-gray-900">Select Section</h3>
              <button 
                onClick={() => setShowSectionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-sm text-gray-500 mb-4">
                Please select a section for <strong>{selectedUnit.unitName}</strong>
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                {selectedUnit.sections.map((section) => (
                  <button
                    key={section._id}
                    onClick={() => handleSectionSelect(section)}
                    className="flex flex-col items-center justify-center p-3 border border-gray-200 rounded-lg transition-all group"
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = 'var(--primary-color, #4f46e5)';
                      e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 5%, white)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = '';
                      e.currentTarget.style.backgroundColor = '';
                    }}
                  >
                    <span className="text-lg font-bold text-gray-700">
                      {section.name || section.sectionName}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const LoadingGrid = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {[...Array(6)].map((_, i) => (
      <div key={i} className="bg-white rounded-2xl border border-gray-200 overflow-hidden h-80 animate-pulse">
        <div className="h-40 bg-gray-200" />
        <div className="p-6 space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/2" />
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="h-10 bg-gray-200 rounded w-full mt-4" />
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = () => (
  <div className="text-center py-20">
    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <Search className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-2">No Classes Found</h3>
    <p className="text-gray-500">Try adjusting your search or filters.</p>
  </div>
);

export default ClassSelection;
